import {
  DrillIcon,
  FlaskConicalIcon,
  FuelIcon,
  GlobeIcon,
  GraduationCapIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  TruckIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos del Petróleo y Gas',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con el petróleo y gas puede ser el más transformador o el más confuso. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Conceptos fundamentales del petróleo y gas natural',
      'Qué son los hidrocarburos',
      'Origen y formación general',
      'Propiedades y características básicas',
      'Petróleo crudo y gas natural',
      'Recursos y reservas',
      'Recursos renovables y no renovables',
      'Principales usos de los hidrocarburos',
      'Importancia energética, industrial y económica',
      'Introducción a la cadena de valor',
      'Upstream, midstream y downstream',
      'Panorama general de la industria',
    ],
    result:
      'Tus alumnos entienden el petróleo y gas como una disciplina práctica y relevante, no como un conjunto de términos imposibles de memorizar.',
    icon: FuelIcon,
  },
  {
    number: '2',
    title: 'Geología del Petróleo y Sistemas Petroleros',
    subtitle: 'Todo lo que necesitan saber sobre los fundamentos geológicos del petróleo y gas',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan la geología petrolera sin sobrecarga técnica.',
    items: [
      'Fundamentos de geología del petróleo',
      'Cuencas sedimentarias',
      'Materia orgánica y generación de hidrocarburos',
      'Roca generadora',
      'Roca reservorio',
      'Roca sello',
      'Porosidad y permeabilidad',
      'Migración de hidrocarburos',
      'Trampas geológicas',
      'Acumulaciones de petróleo y gas',
      'Yacimientos',
      'Sistema petrolero',
      'Relación entre los diferentes elementos geológicos',
      '12 casos prácticos de geología petrolera',
    ],
    result: 'Tus alumnos aprenden los principios geológicos que explican dónde se encuentran el petróleo y gas.',
    icon: GlobeIcon,
  },
  {
    number: '3',
    title: 'Exploración de Petróleo y Gas',
    subtitle: 'Cómo se descubren y evalúan los yacimientos de petróleo y gas',
    classroom:
      'La exploración petrolera es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan este proceso.',
    items: [
      'Fundamentos de exploración',
      'Objetivos del proceso exploratorio',
      'Información geológica',
      'Información geofísica',
      'Conceptos generales de sísmica',
      'Caracterización conceptual del subsuelo',
      'Prospectos exploratorios',
      'Hipótesis geológicas',
      'Incertidumbre',
      'Riesgo exploratorio',
      'Evaluación conceptual de recursos',
      'Pozos exploratorios',
      'Integración de diferentes fuentes de información',
      'Toma de decisiones bajo incertidumbre',
      '15 casos prácticos de exploración',
    ],
    result: 'Tus alumnos comprenden cómo se descubren los yacimientos de petróleo y gas y qué información se necesita.',
    icon: SearchIcon,
  },
  {
    number: '4',
    title: 'Perforación y Construcción de Pozos',
    subtitle: 'Cómo se perforan y construyen los pozos petroleros',
    classroom: 'La perforación es el corazón de la industria. Este tomo lo explica de forma clara y accesible.',
    items: [
      'Función de un pozo',
      'Conceptos fundamentales de perforación',
      'Etapas generales',
      'Equipos y componentes principales a nivel conceptual',
      'Revestimiento',
      'Cementación',
      'Integridad del pozo',
      'Pozos verticales',
      'Pozos direccionales',
      'Pozos horizontales',
      'Terminación conceptual',
      'Monitoreo',
      'Seguridad',
      'Ciclo de vida de un pozo',
      '12 casos prácticos de perforación',
    ],
    result: 'Tus alumnos entienden cómo se perforan los pozos y los sistemas que garantizan su seguridad.',
    icon: DrillIcon,
  },
  {
    number: '5',
    title: 'Producción de Petróleo y Gas',
    subtitle: 'Cómo se extrae petróleo y gas desde los yacimientos',
    classroom: 'La producción completa el ciclo exploratorio. Este tomo lo hace comprensible y práctico.',
    items: [
      'Fundamentos de producción',
      'Del reservorio a la superficie',
      'Presión del yacimiento',
      'Movimiento de fluidos',
      'Producción de petróleo, gas y agua',
      'Separación inicial',
      'Gas asociado y no asociado',
      'Instalaciones generales de producción',
      'Recuperación primaria',
      'Recuperación secundaria y mejorada a nivel conceptual',
      'Levantamiento artificial a nivel conceptual',
      'Declinación de la producción',
      'Monitoreo',
      'Optimización',
      'Ciclo de vida de un campo',
      '15 casos prácticos de producción',
    ],
    result: 'Tus alumnos comprenden cómo se extrae petróleo y gas y cómo evolucionan los yacimientos.',
    icon: SettingsIcon,
  },
  {
    number: '6',
    title: 'Refinación de Petróleo y Procesamiento de Gas Natural',
    subtitle: 'Cómo se transforman los hidrocarburos en productos valiosos',
    classroom:
      'La refinación y el procesamiento completan la cadena de valor. Este tomo lo hace accesible.',
    items: [
      'Petróleo crudo como materia prima',
      'Introducción a las refinerías',
      'Principios de refinación',
      'Destilación',
      'Separación de fracciones',
      'Procesos de conversión',
      'Tratamiento',
      'Principales derivados del petróleo',
      'Gasolina, diésel, queroseno y otros productos',
      'Petroquímica',
      'Procesamiento del gas natural',
      'Separación y acondicionamiento',
      'GLP',
      'GNL',
      'Relación entre materia prima, proceso, producto y uso',
      '12 casos prácticos de refinación y procesamiento',
    ],
    result: 'Tus alumnos comprenden cómo el petróleo crudo se transforma en productos que usamos diariamente.',
    icon: FlaskConicalIcon,
  },
  {
    number: '7',
    title: 'Transporte, Almacenamiento y Distribución de Petróleo y Gas',
    subtitle: 'La infraestructura que conecta producción con consumo',
    classroom: 'El transporte es la columna vertebral de la industria. Este tomo lo explica claramente.',
    items: [
      'Fundamentos del midstream',
      'Logística del petróleo y gas',
      'Oleoductos',
      'Gasoductos',
      'Poliductos',
      'Transporte terrestre',
      'Transporte marítimo',
      'Terminales',
      'Tanques y almacenamiento',
      'Distribución',
      'Redes e infraestructura',
      'Continuidad del suministro',
      'Integridad de infraestructura',
      'Seguridad y ambiente vinculados al transporte',
      '12 casos prácticos de transporte y distribución',
    ],
    result: 'Tus alumnos entienden cómo el petróleo y gas llegan desde la producción hasta el consumidor.',
    icon: TruckIcon,
  },
  {
    number: '8',
    title: 'Seguridad, Gestión de Riesgos y Medio Ambiente',
    subtitle: 'Las dimensiones fundamentales de la industria responsable',
    classroom: 'La seguridad y el ambiente son prioritarios. Este tomo los aborda de forma integral.',
    items: [
      'Diferencia entre peligro y riesgo',
      'Identificación conceptual de riesgos',
      'Prevención',
      'Mitigación',
      'Barreras de seguridad',
      'Integridad',
      'Factores humanos',
      'Cultura de seguridad',
      'Monitoreo y mantenimiento',
      'Incidentes y aprendizaje',
      'Gestión ambiental',
      'Agua, suelo y residuos',
      'Biodiversidad y ecosistemas',
      'Emisiones y metano',
      'Cambio climático',
      'Prevención y gestión de impactos',
      'Comunidades y entorno',
      '15 casos prácticos de seguridad y ambiente',
    ],
    result:
      'Tus alumnos entienden que la seguridad y el ambiente son científicamente rigurosos y culturalmente prioritarios.',
    icon: ShieldCheckIcon,
  },
  {
    number: '9',
    title: 'Enseñanza del Petróleo y Gas: Estrategias, Actividades y Proyectos Educativos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Cómo enseñar petróleo y gas',
      'Organización progresiva de contenidos',
      'Estrategias pedagógicas',
      'Activación de conocimientos previos',
      'Explicación de conceptos técnicos',
      'Preguntas y discusión',
      'Pensamiento crítico',
      'Análisis de situaciones',
      'Actividades educativas',
      'Propuestas de investigación conceptual',
      'Proyectos educativos',
      'Integración de geología, industria, seguridad y ambiente',
      'Evaluación del aprendizaje',
      'Adaptación según nivel de los estudiantes',
      'Orientaciones para planificar la enseñanza en 2026',
      '10 proyectos educativos listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
